// ActivityTable.tsx

import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TableSortLabel,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { TableWrapper } from '../Activity.styles';

// Interface matching container's data structure
interface ActivityItem {
  id: string;
  name: string;
  team: string;
  firstContactAfter9AM: string | null;
  thirdContactAfter9AM: string | null;
  lastBefore3pm: string | null;
  firstAfter4pm: string | null;
  thirdToLastPin: string | null;
  lastPin: string | null;
  pinsDropped: number | string;
  // New fields from the updated Lambda response
  sales: number | string | null;
  notHomeCount: number | string | null;
  notInterestedCount: number | string | null;
  gavePitchCount: number | string | null;
  callBacksCount: number | string | null;
  percentNotInterested: number | string | null;
  percentGavePitch: number | string | null;
  percentNotHome: number | string | null;
  percentCallBack: number | string | null;
  knocksToSales: number | string | null;
  notInterestedToSales: number | string | null;
  gavePitchToSales: number | string | null;
}

// Define a type for sort order
type Order = 'asc' | 'desc';

// Define a type for sortable property keys
type SortableKey = keyof ActivityItem;

// Interface for userActivityKpis
interface UserActivityKpis {
  notInterested: { id: string };
  knocks: { id: string };
  callBack: { id: string };
  interested: { id: string };
  gavePitch: { id: string };
  sales: { id: string };
  notHome: { id: string };
  [key: string]: { id: string };
}

interface ActivityTableProps {
  loading: boolean;
  activityData: ActivityItem[];
  error: string | null;
  page: number;
  rowsPerPage: number;
  totalCount: number;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: SortableKey;
  handleRequestSort: (property: SortableKey) => void;
  userActivityKpis?: UserActivityKpis;
}

// Helper function to format percentages with % sign
const formatPercent = (value: number | string | null): string => {
  if (value === null || value === '--') return '--';
  return typeof value === 'number' ? `${value}%` : `${value}%`;
};

// Determine if KPIs are available
const areKpisAvailable = (userActivityKpis?: UserActivityKpis): boolean => {
  return (
    userActivityKpis !== undefined && Object.keys(userActivityKpis).length > 0
  );
};

// Helper function to count the number of columns to display
const getColumnCount = (userActivityKpis?: UserActivityKpis) => {
  // Base columns (Name, Team, time tracking columns, Pins Dropped)
  const baseColumnCount = 9;

  // Activity KPI related columns
  const kpiColumnsCount = areKpisAvailable(userActivityKpis) ? 12 : 0;

  return baseColumnCount + kpiColumnsCount;
};

const ActivityTable: React.FC<ActivityTableProps> = ({
  loading,
  activityData,
  error,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  order,
  orderBy,
  handleRequestSort,
  userActivityKpis,
}) => {
  // Get paginated data
  const getPaginatedData = () => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return activityData.slice(startIndex, endIndex);
  };

  // Get the data to display after pagination
  const displayData = getPaginatedData();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
          backgroundColor: (theme) => theme.custom.background2,
          borderRadius: '20px',
          padding: '20px',
        }}
      >
        <Typography
          sx={{
            color: (theme) => theme.custom.textLight,
            fontSize: '1.1rem',
            textAlign: 'center',
          }}
        >
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <TableWrapper>
      <TableContainer
        component={Paper}
        sx={{
          maxWidth: '100%',
          overflowX: 'auto',
          borderRadius: '10px',
          backgroundColor: (theme) => theme.custom.background1,
        }}
      >
        <Table
          sx={{
            minWidth: 650,
            tableLayout: 'fixed',
            '& .sticky-column': {
              position: 'sticky',
              left: 0,
              zIndex: 1,
            },
            '& .sticky-column-header': {
              position: 'sticky',
              left: 0,
              zIndex: 2,
              backgroundColor: (theme) => theme.custom.primary,
            },
            '& tr:nth-of-type(odd) .sticky-column': {
              backgroundColor: (theme) => theme.custom.background3,
            },
            '& tr:nth-of-type(even) .sticky-column': {
              backgroundColor: (theme) => theme.custom.background1,
            },
          }}
          aria-label="activity table"
        >
          <TableHead
            sx={{
              backgroundColor: (theme) => theme.custom.primary,
              fontWeight: 'bold',
            }}
          >
            <TableRow>
              {/* Base columns */}
              <TableCell
                className="sticky-column-header"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  color: (theme) => theme.custom.textCTA,
                  borderBottom: 'none',
                  width: '180px',
                  minWidth: '180px',
                  maxWidth: '180px',
                }}
              >
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                  sx={{
                    '& .MuiTableSortLabel-icon': {
                      color: (theme) => `${theme.custom.textCTA} !important`,
                    },
                    '&.Mui-active': {
                      color: (theme) => `${theme.custom.textCTA} !important`,
                    },
                    color: (theme) => `${theme.custom.textCTA} !important`,
                  }}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  color: (theme) => theme.custom.textCTA,
                  borderBottom: 'none',
                  width: '150px',
                  minWidth: '150px',
                }}
              >
                <TableSortLabel
                  active={orderBy === 'team'}
                  direction={orderBy === 'team' ? order : 'asc'}
                  onClick={() => handleRequestSort('team')}
                  sx={{
                    '& .MuiTableSortLabel-icon': {
                      color: (theme) => `${theme.custom.textCTA} !important`,
                    },
                    '&.Mui-active': {
                      color: (theme) => `${theme.custom.textCTA} !important`,
                    },
                    color: (theme) => `${theme.custom.textCTA} !important`,
                  }}
                >
                  Team
                </TableSortLabel>
              </TableCell>

              {/* Time tracking columns */}
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  color: (theme) => theme.custom.textCTA,
                  borderBottom: 'none',
                  width: '150px',
                  minWidth: '150px',
                }}
              >
                <TableSortLabel
                  active={orderBy === 'firstContactAfter9AM'}
                  direction={orderBy === 'firstContactAfter9AM' ? order : 'asc'}
                  onClick={() => handleRequestSort('firstContactAfter9AM')}
                  sx={{
                    '& .MuiTableSortLabel-icon': {
                      color: (theme) => `${theme.custom.textCTA} !important`,
                    },
                    '&.Mui-active': {
                      color: (theme) => `${theme.custom.textCTA} !important`,
                    },
                    color: (theme) => `${theme.custom.textCTA} !important`,
                  }}
                >
                  First After 9am
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  color: (theme) => theme.custom.textCTA,
                  borderBottom: 'none',
                  width: '150px',
                  minWidth: '150px',
                }}
              >
                <TableSortLabel
                  active={orderBy === 'thirdContactAfter9AM'}
                  direction={orderBy === 'thirdContactAfter9AM' ? order : 'asc'}
                  onClick={() => handleRequestSort('thirdContactAfter9AM')}
                  sx={{
                    '& .MuiTableSortLabel-icon': {
                      color: (theme) => `${theme.custom.textCTA} !important`,
                    },
                    '&.Mui-active': {
                      color: (theme) => `${theme.custom.textCTA} !important`,
                    },
                    color: (theme) => `${theme.custom.textCTA} !important`,
                  }}
                >
                  Third After 9am
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  color: (theme) => theme.custom.textCTA,
                  borderBottom: 'none',
                  width: '150px',
                  minWidth: '150px',
                }}
              >
                <TableSortLabel
                  active={orderBy === 'lastBefore3pm'}
                  direction={orderBy === 'lastBefore3pm' ? order : 'asc'}
                  onClick={() => handleRequestSort('lastBefore3pm')}
                  sx={{
                    '& .MuiTableSortLabel-icon': {
                      color: (theme) => `${theme.custom.textCTA} !important`,
                    },
                    '&.Mui-active': {
                      color: (theme) => `${theme.custom.textCTA} !important`,
                    },
                    color: (theme) => `${theme.custom.textCTA} !important`,
                  }}
                >
                  Last Before 3pm
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  color: (theme) => theme.custom.textCTA,
                  borderBottom: 'none',
                  width: '150px',
                  minWidth: '150px',
                }}
              >
                <TableSortLabel
                  active={orderBy === 'firstAfter4pm'}
                  direction={orderBy === 'firstAfter4pm' ? order : 'asc'}
                  onClick={() => handleRequestSort('firstAfter4pm')}
                  sx={{
                    '& .MuiTableSortLabel-icon': {
                      color: (theme) => `${theme.custom.textCTA} !important`,
                    },
                    '&.Mui-active': {
                      color: (theme) => `${theme.custom.textCTA} !important`,
                    },
                    color: (theme) => `${theme.custom.textCTA} !important`,
                  }}
                >
                  First After 4pm
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  color: (theme) => theme.custom.textCTA,
                  borderBottom: 'none',
                  width: '150px',
                  minWidth: '150px',
                }}
              >
                <TableSortLabel
                  active={orderBy === 'thirdToLastPin'}
                  direction={orderBy === 'thirdToLastPin' ? order : 'asc'}
                  onClick={() => handleRequestSort('thirdToLastPin')}
                  sx={{
                    '& .MuiTableSortLabel-icon': {
                      color: (theme) => `${theme.custom.textCTA} !important`,
                    },
                    '&.Mui-active': {
                      color: (theme) => `${theme.custom.textCTA} !important`,
                    },
                    color: (theme) => `${theme.custom.textCTA} !important`,
                  }}
                >
                  Third To Last Pin
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  color: (theme) => theme.custom.textCTA,
                  borderBottom: 'none',
                  width: '150px',
                  minWidth: '150px',
                }}
              >
                <TableSortLabel
                  active={orderBy === 'lastPin'}
                  direction={orderBy === 'lastPin' ? order : 'asc'}
                  onClick={() => handleRequestSort('lastPin')}
                  sx={{
                    '& .MuiTableSortLabel-icon': {
                      color: (theme) => `${theme.custom.textCTA} !important`,
                    },
                    '&.Mui-active': {
                      color: (theme) => `${theme.custom.textCTA} !important`,
                    },
                    color: (theme) => `${theme.custom.textCTA} !important`,
                  }}
                >
                  Last Pin
                </TableSortLabel>
              </TableCell>

              {/* Basic activity count - always show */}
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  color: (theme) => theme.custom.textCTA,
                  borderBottom: 'none',
                  width: '150px',
                  minWidth: '150px',
                }}
              >
                <TableSortLabel
                  active={orderBy === 'pinsDropped'}
                  direction={orderBy === 'pinsDropped' ? order : 'asc'}
                  onClick={() => handleRequestSort('pinsDropped')}
                  sx={{
                    '& .MuiTableSortLabel-icon': {
                      color: (theme) => `${theme.custom.textCTA} !important`,
                    },
                    '&.Mui-active': {
                      color: (theme) => `${theme.custom.textCTA} !important`,
                    },
                    color: (theme) => `${theme.custom.textCTA} !important`,
                  }}
                >
                  Pins Dropped
                </TableSortLabel>
              </TableCell>

              {/* Activity counts - only show if KPIs are available */}
              {areKpisAvailable(userActivityKpis) && (
                <>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      color: (theme) => theme.custom.textCTA,
                      borderBottom: 'none',
                      width: '150px',
                      minWidth: '150px',
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'sales'}
                      direction={orderBy === 'sales' ? order : 'asc'}
                      onClick={() => handleRequestSort('sales')}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          color: (theme) =>
                            `${theme.custom.textCTA} !important`,
                        },
                        '&.Mui-active': {
                          color: (theme) =>
                            `${theme.custom.textCTA} !important`,
                        },
                        color: (theme) => `${theme.custom.textCTA} !important`,
                      }}
                    >
                      Sales
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      color: (theme) => theme.custom.textCTA,
                      borderBottom: 'none',
                      width: '150px',
                      minWidth: '150px',
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'notHomeCount'}
                      direction={orderBy === 'notHomeCount' ? order : 'asc'}
                      onClick={() => handleRequestSort('notHomeCount')}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          color: (theme) =>
                            `${theme.custom.textCTA} !important`,
                        },
                        '&.Mui-active': {
                          color: (theme) =>
                            `${theme.custom.textCTA} !important`,
                        },
                        color: (theme) => `${theme.custom.textCTA} !important`,
                      }}
                    >
                      Not Home
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      color: (theme) => theme.custom.textCTA,
                      borderBottom: 'none',
                      width: '150px',
                      minWidth: '150px',
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'notInterestedCount'}
                      direction={
                        orderBy === 'notInterestedCount' ? order : 'asc'
                      }
                      onClick={() => handleRequestSort('notInterestedCount')}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          color: (theme) =>
                            `${theme.custom.textCTA} !important`,
                        },
                        '&.Mui-active': {
                          color: (theme) =>
                            `${theme.custom.textCTA} !important`,
                        },
                        color: (theme) => `${theme.custom.textCTA} !important`,
                      }}
                    >
                      Not Interested
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      color: (theme) => theme.custom.textCTA,
                      borderBottom: 'none',
                      width: '150px',
                      minWidth: '150px',
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'gavePitchCount'}
                      direction={orderBy === 'gavePitchCount' ? order : 'asc'}
                      onClick={() => handleRequestSort('gavePitchCount')}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          color: (theme) =>
                            `${theme.custom.textCTA} !important`,
                        },
                        '&.Mui-active': {
                          color: (theme) =>
                            `${theme.custom.textCTA} !important`,
                        },
                        color: (theme) => `${theme.custom.textCTA} !important`,
                      }}
                    >
                      Gave Pitch
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      color: (theme) => theme.custom.textCTA,
                      borderBottom: 'none',
                      width: '150px',
                      minWidth: '150px',
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'callBacksCount'}
                      direction={orderBy === 'callBacksCount' ? order : 'asc'}
                      onClick={() => handleRequestSort('callBacksCount')}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          color: (theme) =>
                            `${theme.custom.textCTA} !important`,
                        },
                        '&.Mui-active': {
                          color: (theme) =>
                            `${theme.custom.textCTA} !important`,
                        },
                        color: (theme) => `${theme.custom.textCTA} !important`,
                      }}
                    >
                      Call Backs
                    </TableSortLabel>
                  </TableCell>

                  {/* Percentage columns */}
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      color: (theme) => theme.custom.textCTA,
                      borderBottom: 'none',
                      width: '150px',
                      minWidth: '150px',
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'percentNotInterested'}
                      direction={
                        orderBy === 'percentNotInterested' ? order : 'asc'
                      }
                      onClick={() => handleRequestSort('percentNotInterested')}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          color: (theme) =>
                            `${theme.custom.textCTA} !important`,
                        },
                        '&.Mui-active': {
                          color: (theme) =>
                            `${theme.custom.textCTA} !important`,
                        },
                        color: (theme) => `${theme.custom.textCTA} !important`,
                      }}
                    >
                      % of total not interested
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      color: (theme) => theme.custom.textCTA,
                      borderBottom: 'none',
                      width: '150px',
                      minWidth: '150px',
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'percentGavePitch'}
                      direction={orderBy === 'percentGavePitch' ? order : 'asc'}
                      onClick={() => handleRequestSort('percentGavePitch')}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          color: (theme) =>
                            `${theme.custom.textCTA} !important`,
                        },
                        '&.Mui-active': {
                          color: (theme) =>
                            `${theme.custom.textCTA} !important`,
                        },
                        color: (theme) => `${theme.custom.textCTA} !important`,
                      }}
                    >
                      % of people spoken to gave pitch
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      color: (theme) => theme.custom.textCTA,
                      borderBottom: 'none',
                      width: '150px',
                      minWidth: '150px',
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'percentNotHome'}
                      direction={orderBy === 'percentNotHome' ? order : 'asc'}
                      onClick={() => handleRequestSort('percentNotHome')}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          color: (theme) =>
                            `${theme.custom.textCTA} !important`,
                        },
                        '&.Mui-active': {
                          color: (theme) =>
                            `${theme.custom.textCTA} !important`,
                        },
                        color: (theme) => `${theme.custom.textCTA} !important`,
                      }}
                    >
                      % of total not home
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      color: (theme) => theme.custom.textCTA,
                      borderBottom: 'none',
                      width: '150px',
                      minWidth: '150px',
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'percentCallBack'}
                      direction={orderBy === 'percentCallBack' ? order : 'asc'}
                      onClick={() => handleRequestSort('percentCallBack')}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          color: (theme) =>
                            `${theme.custom.textCTA} !important`,
                        },
                        '&.Mui-active': {
                          color: (theme) =>
                            `${theme.custom.textCTA} !important`,
                        },
                        color: (theme) => `${theme.custom.textCTA} !important`,
                      }}
                    >
                      % of people spoken to call back
                    </TableSortLabel>
                  </TableCell>

                  {/* Ratio columns */}
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      color: (theme) => theme.custom.textCTA,
                      borderBottom: 'none',
                      width: '150px',
                      minWidth: '150px',
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'knocksToSales'}
                      direction={orderBy === 'knocksToSales' ? order : 'asc'}
                      onClick={() => handleRequestSort('knocksToSales')}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          color: (theme) =>
                            `${theme.custom.textCTA} !important`,
                        },
                        '&.Mui-active': {
                          color: (theme) =>
                            `${theme.custom.textCTA} !important`,
                        },
                        color: (theme) => `${theme.custom.textCTA} !important`,
                      }}
                    >
                      Knocks Vs. Sales
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      color: (theme) => theme.custom.textCTA,
                      borderBottom: 'none',
                      width: '170px',
                      minWidth: '170px',
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'notInterestedToSales'}
                      direction={
                        orderBy === 'notInterestedToSales' ? order : 'asc'
                      }
                      onClick={() => handleRequestSort('notInterestedToSales')}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          color: (theme) =>
                            `${theme.custom.textCTA} !important`,
                        },
                        '&.Mui-active': {
                          color: (theme) =>
                            `${theme.custom.textCTA} !important`,
                        },
                        color: (theme) => `${theme.custom.textCTA} !important`,
                      }}
                    >
                      Not Interested Vs. Sales
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      color: (theme) => theme.custom.textCTA,
                      borderBottom: 'none',
                      width: '150px',
                      minWidth: '150px',
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'gavePitchToSales'}
                      direction={orderBy === 'gavePitchToSales' ? order : 'asc'}
                      onClick={() => handleRequestSort('gavePitchToSales')}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          color: (theme) =>
                            `${theme.custom.textCTA} !important`,
                        },
                        '&.Mui-active': {
                          color: (theme) =>
                            `${theme.custom.textCTA} !important`,
                        },
                        color: (theme) => `${theme.custom.textCTA} !important`,
                      }}
                    >
                      Gave Pitch Vs. Sales
                    </TableSortLabel>
                  </TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayData.length > 0 ? (
              displayData.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:nth-of-type(odd)': {
                      backgroundColor: (theme) => theme.custom.background3,
                    },
                  }}
                >
                  <TableCell
                    component="th"
                    scope="row"
                    className="sticky-column"
                    sx={{
                      boxShadow: (theme) =>
                        theme.custom.isDark
                          ? '2px 0px 3px rgba(0,0,0,0.3)'
                          : '2px 0px 3px rgba(0,0,0,0.1)',
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      color: (theme) => theme.custom.text,
                    }}
                  >
                    {row.name}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '1rem',
                      color: (theme) => theme.custom.text,
                    }}
                  >
                    {row.team}
                  </TableCell>

                  {/* Time tracking data */}
                  <TableCell
                    sx={{
                      fontSize: '1rem',
                      color: (theme) => theme.custom.text,
                    }}
                  >
                    {row.firstContactAfter9AM || '--'}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '1rem',
                      color: (theme) => theme.custom.text,
                    }}
                  >
                    {row.thirdContactAfter9AM || '--'}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '1rem',
                      color: (theme) => theme.custom.text,
                    }}
                  >
                    {row.lastBefore3pm || '--'}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '1rem',
                      color: (theme) => theme.custom.text,
                    }}
                  >
                    {row.firstAfter4pm || '--'}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '1rem',
                      color: (theme) => theme.custom.text,
                    }}
                  >
                    {row.thirdToLastPin || '--'}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '1rem',
                      color: (theme) => theme.custom.text,
                    }}
                  >
                    {row.lastPin || '--'}
                  </TableCell>

                  {/* Activity counts data - basic */}
                  <TableCell
                    sx={{
                      fontSize: '1rem',
                      color: (theme) => theme.custom.text,
                    }}
                  >
                    {row.pinsDropped}
                  </TableCell>

                  {/* Activity counts data - only if KPIs are available */}
                  {areKpisAvailable(userActivityKpis) && (
                    <>
                      <TableCell
                        sx={{
                          fontSize: '1rem',
                          color: (theme) => theme.custom.text,
                        }}
                      >
                        {row.sales}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '1rem',
                          color: (theme) => theme.custom.text,
                        }}
                      >
                        {row.notHomeCount}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '1rem',
                          color: (theme) => theme.custom.text,
                        }}
                      >
                        {row.notInterestedCount}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '1rem',
                          color: (theme) => theme.custom.text,
                        }}
                      >
                        {row.gavePitchCount}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '1rem',
                          color: (theme) => theme.custom.text,
                        }}
                      >
                        {row.callBacksCount}
                      </TableCell>

                      {/* Percentage data */}
                      <TableCell
                        sx={{
                          fontSize: '1rem',
                          color: (theme) => theme.custom.text,
                        }}
                      >
                        {row.percentNotInterested !== null &&
                        row.percentNotInterested !== '--'
                          ? formatPercent(row.percentNotInterested)
                          : '--'}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '1rem',
                          color: (theme) => theme.custom.text,
                        }}
                      >
                        {row.percentGavePitch !== null &&
                        row.percentGavePitch !== '--'
                          ? formatPercent(row.percentGavePitch)
                          : '--'}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '1rem',
                          color: (theme) => theme.custom.text,
                        }}
                      >
                        {row.percentNotHome !== null &&
                        row.percentNotHome !== '--'
                          ? formatPercent(row.percentNotHome)
                          : '--'}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '1rem',
                          color: (theme) => theme.custom.text,
                        }}
                      >
                        {row.percentCallBack !== null &&
                        row.percentCallBack !== '--'
                          ? formatPercent(row.percentCallBack)
                          : '--'}
                      </TableCell>

                      {/* Ratio data */}
                      <TableCell
                        sx={{
                          fontSize: '1rem',
                          color: (theme) => theme.custom.text,
                        }}
                      >
                        {row.knocksToSales}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '1rem',
                          color: (theme) => theme.custom.text,
                        }}
                      >
                        {row.notInterestedToSales}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '1rem',
                          color: (theme) => theme.custom.text,
                        }}
                      >
                        {row.gavePitchToSales}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={getColumnCount(userActivityKpis)}
                  align="center"
                  sx={{
                    fontSize: '1rem',
                    color: (theme) => theme.custom.textLight,
                  }}
                >
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={activityData.length}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[25]}
      />
    </TableWrapper>
  );
};

export default ActivityTable;
